import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { useTable, usePagination, useSortBy } from 'react-table'
import { useNavigate, useSearchParams } from 'react-router-dom'
import debounce from 'lodash/debounce'
import toast from 'react-hot-toast'

import styles from './ReleaseNotesList.module.css'

import PageLoader from 'Layout/PageLoader'
import Table, { TableActions, TableBody, TableHead, TableRow, Td, Th } from 'Design/components/Table'
import Input from 'Design/components/Input'
import Pagination from 'Design/components/Pagination'
import ConfirmationModal from 'Design/components/ConfirmationModal'

import NoReleaseNotes from './NoReleaseNotes'
import ReleaseNoteOptions from './ReleaseNoteOptions'
import { useDeleteReleaseNoteMutation, useListReleaseNotesQuery } from '../release-notes.service'
import ReactToolTip from '../../../../../components/ReactTooltip/ReactTooltip'
import { CATEGORIES } from '../constants'

import * as date from 'Utils/date'
import parseErrorMessage from 'Utils/error-message-parser'

const DEFAULT_PAGE_SIZE = 15
const DEFAULT_PAGE = 1
const SUPPORTED_SORT_COLUMNS = ['created_at', 'updated_at']
const DEFAULT_SORTED_COLUMN = 'created_at'
const DEFAULT_SORT_ORDER = 'desc'
const SUPPORTED_SORT_ORDER = ['asc', 'desc']

const getDefaultParams = () => {
  const params = new URLSearchParams(location.search)

  const paramsSize = params.get('size') || DEFAULT_PAGE_SIZE
  const paramsPage = params.get('page') || DEFAULT_PAGE
  const paramsSearch = params.get('search') ?? ''
  const paramsSortBy = params.get('sort_by') ?? DEFAULT_SORTED_COLUMN
  const paramsOrderBy = params.get('order_by') ?? DEFAULT_SORT_ORDER

  const page = isNaN(paramsPage) ? DEFAULT_PAGE : parseInt(paramsPage)
  const size = isNaN(paramsSize) ? DEFAULT_PAGE_SIZE : parseInt(paramsSize)

  const sortColumn = SUPPORTED_SORT_COLUMNS.includes(paramsSortBy) ? paramsSortBy : DEFAULT_SORTED_COLUMN
  const sortOrder = SUPPORTED_SORT_ORDER.includes(paramsOrderBy) ? paramsOrderBy : DEFAULT_SORT_ORDER

  return {
    search: paramsSearch,
    page,
    size,
    sortBy: [{ id: sortColumn, desc: sortOrder === 'desc' }]
  }
}

const getInitialApiSearchParams = (params) => {
  return {
    offset: (params.page - 1) * params.size,
    limit: params.size,
    sort_by: params.sortBy[0].id,
    order_by: params.sortBy[0].desc ? 'desc' : 'asc',
    search: params.search
  }
}

const ReleaseNotesTable = () => {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()

  const initialParams = useMemo(getDefaultParams, [])

  const [queryParams, setQueryParams] = useState(getInitialApiSearchParams(initialParams))
  const [searchText, setSearchText] = useState(initialParams.search)
  const [pageSize] = useState(initialParams.size)
  const [selectedItem, setSelectedItem] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { data = {}, isLoading } = useListReleaseNotesQuery(queryParams)
  const [deleteReleaseNote, deleteReleaseNoteResult] = useDeleteReleaseNoteMutation()
  const { results: releaseNotes = [], meta: paginationDetails = {} } = data

  const debouncedSetQueryParams = useMemo(() => {
    return debounce(setQueryParams, 500, { leading: true, trailing: true })
  }, [])

  const columns = useMemo(() => {
    return [
      {
        Header: 'Title',
        accessor: 'title',
        disableSortBy: true
      },
      {
        Header: 'Categories',
        accessor: 'categories',
        disableSortBy: true,
        Cell: ({ cell: { value = [] } }) => {
          return value
            .map((categoryName) => {
              return CATEGORIES[categoryName] ?? 'Unknown Category'
            })
            .join(', ')
        }
      },
      {
        Header: 'Created on',
        accessor: 'created_at',
        disableSortBy: false,
        Cell: ({ cell: { value } }) => {
          return date.formatRelativeDate(value, 7)
        },
        Tooltip: ({ cell: { value } }) => {
          return date.formatLongDateTime(value)
        }
      },
      {
        Header: 'Last updated on',
        disableSortBy: false,
        accessor: 'updated_at',
        Cell: ({ cell: { value } }) => {
          return value ? date.formatRelativeDate(value, 7) : '-'
        },
        Tooltip: ({ cell: { value } }) => {
          if (!value) {
            return null
          }

          return date.formatLongDateTime(value)
        }
      },
      {
        Header: 'Visible',
        accessor: 'is_visible',
        disableSortBy: false,
        Cell: ({ cell: { value } }) => {
          return value ? 'Yes' : 'No'
        }
      }
    ]
  }, [])

  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
    state: { pageIndex, sortBy }
  } = useTable(
    {
      columns,
      data: releaseNotes,
      initialState: {
        pageIndex: initialParams.page - 1,
        pageSize,
        sortBy: initialParams.sortBy
      },
      // pagination
      manualPagination: true,
      pageCount: Math.ceil(paginationDetails.total / pageSize),
      // sort
      disableSortRemove: true,
      manualSortBy: true,
      autoResetSortBy: false
    },
    useSortBy,
    usePagination
  )

  useEffect(() => {
    const params = {
      sort_by: '',
      order_by: '',
      search: searchText
    }

    sortBy.forEach((sort) => {
      params.sort_by = sort.id
      params.order_by = sort.desc ? 'desc' : 'asc'
    })

    const apiParams = {
      offset: pageIndex * pageSize,
      limit: pageSize,
      ...params
    }

    const urlPrams = {
      page: pageIndex + 1,
      size: pageSize,
      ...params
    }

    debouncedSetQueryParams(apiParams)
    setSearchParams(urlPrams, { replace: true })
  }, [pageIndex, pageSize, searchText, sortBy, debouncedSetQueryParams, setSearchParams])

  const handleSearchInputChange = (e) => {
    gotoPage(0)
    setSearchText(e.target.value)
  }

  const onDeleteReleaseNote = async (id) => {
    const promise = deleteReleaseNote(id).unwrap()

    toast.promise(promise, {
      loading: 'Deleting Releaes Note...',
      success: 'Release Note deleted successfully.',
      error: parseErrorMessage('Unable to delete release note. Try again...')
    })

    promise.then(handleCloseDeleteModal)
  }

  const handleOpenDeleteModal = () => {
    setShowDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
  }

  const handleDeleteClick = () => {
    onDeleteReleaseNote(selectedItem.id)
  }

  if (isLoading) {
    return <PageLoader />
  }

  const isDeleting = deleteReleaseNoteResult.isLoading

  return (
    <div className={styles.Container}>
      <ConfirmationModal
        isOpen={showDeleteModal}
        title='Delete Release Note'
        onCloseButtonClick={handleCloseDeleteModal}
        onConfirmButtonClick={handleDeleteClick}
        confirmButtonText='Yes'
        cancelButtonText='No'
        disableConfirmButton={isDeleting}
      >
        Are you sure you want to delete the release note <strong>{selectedItem?.title}</strong>?
      </ConfirmationModal>

      <TableActions>
        <div className={styles.SearchInputContainer}>
          <Input placeholder='Enter Release Notes Title' value={searchText} onChange={handleSearchInputChange} />
        </div>
        <div className={styles.PaginationContainer}>
          <Pagination
            currentPage={pageIndex + 1}
            pageSize={pageSize}
            totalItems={paginationDetails.total}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            previousPage={previousPage}
            nextPage={nextPage}
          />
        </div>
      </TableActions>
      <Table className={styles.ReleaseNotesTable} {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => {
            const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps()

            return (
              <TableRow key={key} {...headerGroupProps}>
                <Fragment>
                  {headerGroup.headers.map((column) => {
                    const { key, ...headerProps } = column.getHeaderProps(column.getSortByToggleProps())

                    return (
                      <Th key={key} isSorted={column.isSorted} isSortedDesc={column.isSortedDesc} {...headerProps}>
                        {column.render('Header')}
                      </Th>
                    )
                  })}
                  <Th isOption />
                </Fragment>
              </TableRow>
            )
          })}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...rowProps } = row.getRowProps()
            const item = row.original

            return (
              <TableRow key={key} {...rowProps} clickable>
                {row.cells.map((cell) => {
                  const { key, ...cellProps } = cell.getCellProps()
                  const isDateColumn = key.includes('created_at') || key.includes('updated_at')
                  return (
                    <Td key={key} {...cellProps} onClick={() => navigate(item.id)}>
                      {isDateColumn && (
                        <div className='date-cell-container'>
                          {/* Render cell value with the expected date format in tooltip */}
                          {cell.value && (
                            <ReactToolTip
                              className='bot-date-tooltip'
                              side='top'
                              content={
                                <div className='bot-date-tooltip-content' onClick={(e) => e.stopPropagation()}>
                                  {cell.render('Tooltip')}
                                </div>
                              }
                            >
                              {cell.render('Cell')}
                            </ReactToolTip>
                          )}
                          {/* Render '-' if cell has no value without tooltip */}
                          {!cell.value && cell.render('Cell')}
                        </div>
                      )}
                      {!isDateColumn && cell.render('Cell')}
                    </Td>
                  )
                })}
                <Td onClick={() => setSelectedItem(item)}>
                  <ReleaseNoteOptions id={item.id} onDeleteClick={handleOpenDeleteModal} />
                </Td>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {rows.length === 0 && <div className={styles.NoFilterResults}>No results found for given query</div>}
    </div>
  )
}

const ReleaseNotesList = () => {
  const { data = {}, isLoading } = useListReleaseNotesQuery()

  if (isLoading) {
    return <PageLoader />
  }

  if (data.results?.length === 0) {
    return <NoReleaseNotes />
  }

  return <ReleaseNotesTable />
}

export default ReleaseNotesList
